const sentInThisTab = new Set();
const pending = new Map();

const MAX_ATTEMPTS = 60;
const RETRY_DELAY_MS = 500;
const LOG_ENABLED = process.env.REACT_APP_META_PIXEL_LOGGING === "true";

const logPixel = (status, name, eventId, details = {}) => {
  if (!LOG_ENABLED || typeof console === "undefined") return;
  const method = status === "DUPLICATE_BLOCKED" || status === "PIXEL_FAILED"
    ? "warn"
    : "info";
  if (typeof console[method] !== "function") return;
  console[method]("[Tap2Cash Meta Pixel]", {
    status,
    event: name,
    eventId,
    timestamp: new Date().toISOString(),
    ...details,
  });
};

export const confirmedLeadEventId = (serviceRequestId) => {
  const id = Number(serviceRequestId);
  return Number.isSafeInteger(id) && id > 0 ? `t2c-lead-${id}` : null;
};

const emitOnce = (name, eventId, retryIfNotReady = true) => {
  if (!eventId || typeof window === "undefined") return false;
  logPixel("ATTEMPT", name, eventId);
  if (sentInThisTab.has(eventId)) {
    logPixel("DUPLICATE_BLOCKED", name, eventId, { reason: "already_sent_in_tab" });
    return false;
  }
  const key = `tap2cash:meta:${eventId}`;
  try {
    if (window.sessionStorage.getItem(key) === "sent") {
      logPixel("DUPLICATE_BLOCKED", name, eventId, { reason: "already_sent_in_session" });
      return false;
    }
  } catch {}

  const emit = (attempt) => {
    if (sentInThisTab.has(eventId)) return false;
    try {
      if (window.sessionStorage.getItem(key) === "sent") {
        pending.delete(eventId);
        return false;
      }
    } catch {}
    let failed = false;
    if (typeof window.fbq === "function") {
      try {
        window.fbq("track", name, {}, { eventID: eventId });
        sentInThisTab.add(eventId);
        pending.delete(eventId);
        try { window.sessionStorage.setItem(key, "sent"); } catch {}
        logPixel("PIXEL_CALLED", name, eventId, { attempt });
        return true;
      } catch {
        failed = true;
        if (attempt === 1 && retryIfNotReady && attempt < MAX_ATTEMPTS) {
          logPixel("PIXEL_FAILED", name, eventId, { attempt, reason: "fbq_exception_retrying" });
        }
      }
    }
    if (retryIfNotReady && attempt < MAX_ATTEMPTS) {
      if (!pending.has(eventId)) {
        if (attempt === 1 && !failed) {
          logPixel("WAITING_FOR_PIXEL", name, eventId, { retryEveryMs: RETRY_DELAY_MS });
        }
        const timer = window.setTimeout(() => {
          pending.delete(eventId);
          emit(attempt + 1);
        }, RETRY_DELAY_MS);
        pending.set(eventId, timer);
      }
    } else {
      logPixel("PIXEL_FAILED", name, eventId, {
        attempt,
        reason: failed ? "fbq_exception" : "fbq_unavailable",
      });
    }
    return false;
  };
  return emit(1);
};

export const trackConfirmedMetaLead = (serviceRequestId, serviceNo) => {
  if (!serviceNo) return false;
  const eventId = confirmedLeadEventId(serviceRequestId);
  return eventId ? emitOnce("Lead", eventId) : false;
};

const positiveId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export const trackMetaRegistration = (userId) => {
  const id = positiveId(userId);
  return id !== null && emitOnce("CompleteRegistration", `t2c-registration-${id}`);
};

export const trackMetaPriceCheck = (evaluationId) => {
  const id = positiveId(evaluationId);
  return id !== null && emitOnce("InitiateCheckout", `t2c-price-${id}`);
};

export const trackMetaPageView = (navigationId) => {
  if (process.env.REACT_APP_META_MANUAL_PAGEVIEW !== "true") return false;
  if (typeof navigationId !== "string" || !/^[a-f0-9-]{36}$/i.test(navigationId)) return false;
  return emitOnce("PageView", `t2c-page-${navigationId}`);
};
